import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { ProjectExchange } from '@/lib/types/project-graph'

// Panel configuration
export type PanelId = 'left' | 'right' | 'bottom'
export type CenterTab = 'graph' | 'sankey'
export type BottomTab = 'feed' | 'console' | 'validation'
export type LeftTab = 'explorer' | 'library'
export type RightTab = 'activity' | 'background' | 'impact'

interface PanelState {
  collapsed: boolean
  size: number // pixels for left/right, percentage for bottom
}

interface WorkspaceStore {
  // Panel states
  panels: Record<PanelId, PanelState>

  // Active tabs
  centerTab: CenterTab
  bottomTab: BottomTab
  leftTab: LeftTab
  rightTab: RightTab

  // Selected node for details panel
  selectedNodeId: string | null
  selectedNodeType: 'activity' | 'exchange' | null

  // Current project
  currentProjectId: string | null

  // Synthetic exchanges for ecoinvent UPs clicked from the Attribution Sankey
  // detail box. Keyed by `bg::<activity_code>` (the same id used as
  // selectedNodeId). Lets BackgroundDatasetView's existing exchange lookup
  // resolve UPs that aren't represented as a real project exchange.
  bgSynthExchanges: Record<string, ProjectExchange>

  // Actions - Panels
  togglePanel: (panelId: PanelId) => void
  setPanel: (panelId: PanelId, state: Partial<PanelState>) => void
  setPanelSize: (panelId: PanelId, size: number) => void

  // Actions - Tabs
  setCenterTab: (tab: CenterTab) => void
  setBottomTab: (tab: BottomTab) => void
  setLeftTab: (tab: LeftTab) => void
  setRightTab: (tab: RightTab) => void

  // Actions - Selection
  selectNode: (nodeId: string, nodeType: 'activity' | 'exchange') => void
  clearSelection: () => void

  // Actions - Background synth exchanges
  upsertBgSynthExchange: (exchange: ProjectExchange) => void

  // Actions - Project
  setCurrentProject: (projectId: string | null) => void
}

// Default panel sizes (pixels for left/right, percentage for bottom)
const DEFAULT_PANEL_SIZES = {
  left: 280,
  right: 320,
  bottom: 200,
} as const

const PANEL_CONSTRAINTS = {
  left: { min: 200, max: 400 },
  right: { min: 280, max: 500 },
  bottom: { min: 120, max: 400 },
} as const

export const useWorkspaceStore = create<WorkspaceStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial panel states
        panels: {
          left: { collapsed: false, size: DEFAULT_PANEL_SIZES.left },
          right: { collapsed: false, size: DEFAULT_PANEL_SIZES.right },
          bottom: { collapsed: false, size: DEFAULT_PANEL_SIZES.bottom },
        },

        // Initial tabs
        centerTab: 'graph',
        bottomTab: 'feed',
        leftTab: 'explorer',
        rightTab: 'activity',

        // Selection
        selectedNodeId: null,
        selectedNodeType: null,

        bgSynthExchanges: {},

        // Project
        currentProjectId: null,

        // Panel actions
        togglePanel: (panelId) =>
          set((state) => ({
            panels: {
              ...state.panels,
              [panelId]: {
                ...state.panels[panelId],
                collapsed: !state.panels[panelId].collapsed,
              },
            },
          })),

        setPanel: (panelId, newState) =>
          set((state) => ({
            panels: {
              ...state.panels,
              [panelId]: {
                ...state.panels[panelId],
                ...newState,
              },
            },
          })),

        setPanelSize: (panelId, size) =>
          set((state) => {
            const constraints = PANEL_CONSTRAINTS[panelId]
            const clampedSize = Math.max(constraints.min, Math.min(constraints.max, size))
            return {
              panels: {
                ...state.panels,
                [panelId]: {
                  ...state.panels[panelId],
                  size: clampedSize,
                },
              },
            }
          }),

        // Tab actions
        setCenterTab: (tab) => set({ centerTab: tab }),
        setBottomTab: (tab) => set({ bottomTab: tab }),
        setLeftTab: (tab) => set({ leftTab: tab }),
        setRightTab: (tab) => set({ rightTab: tab }),

        // Selection actions
        selectNode: (nodeId, nodeType) =>
          set({
            selectedNodeId: nodeId,
            selectedNodeType: nodeType,
          }),

        clearSelection: () =>
          set({
            selectedNodeId: null,
            selectedNodeType: null,
          }),

        upsertBgSynthExchange: (exchange) =>
          set((state) => ({
            bgSynthExchanges: { ...state.bgSynthExchanges, [exchange.id]: exchange },
          })),

        // Project actions
        setCurrentProject: (projectId) => set({ currentProjectId: projectId }),
      }),
      {
        name: 'ecosemantic-workspace',
        partialize: (state) => ({
          panels: state.panels,
          centerTab: state.centerTab,
          bottomTab: state.bottomTab,
          leftTab: state.leftTab,
          rightTab: state.rightTab,
          // Don't persist selection or current project
        }),
        // Migrate stale persisted tab values: 'table' centerTab (removed 2026-05-09),
        // 'report' rightTab (removed 2026-07-06 — reports get their own view).
        merge: (persisted, current) => {
          const p = { ...((persisted as Partial<WorkspaceStore> | undefined) ?? {}) }
          if ((p.centerTab as string) === 'table') p.centerTab = 'graph'
          if ((p.rightTab as string) === 'report') p.rightTab = 'activity'
          return { ...current, ...p }
        },
      },
    ),
    { name: 'workspace-store' },
  ),
)

// Export constraints for use in components
export const WORKSPACE_PANEL_CONSTRAINTS = PANEL_CONSTRAINTS
export const WORKSPACE_DEFAULT_SIZES = DEFAULT_PANEL_SIZES
